// @file The store for magic padlet panel displayed by DashLayoutPickerPage.vue and LayoutPickerDesktop.vue
import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { isExamplePrompt, MAGIC_TEMPLATES } from '@@/bits/magic_padlet_helper'
import { poll } from '@@/bits/polling'
import safeStorage from '@@/bits/safe_storage'
import type { PopoverAnchor } from '@@/library/v4/components/OzPopoverModal.vue'
import PadletApi from '@@/surface/padlet_api'
import type { LibraryId, MagicTemplate, MagicWallCreationResult, MagicWallCreationStatusResult } from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { FetchError, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

enum MagicPadletPanelStatus {
  Initial = 'Initial',
  Creating = 'Creating',
  Errored = 'Errored',
  AtQuota = 'AtQuota',
}

const magicTemplateEnum: Record<string, MagicTemplate> = {
  LessonPlan: {
    name: __('Lesson plan'),
    key: 'lesson_plan',
    icon: 'note_outline',
  },
  ClassroomActivitiesList: {
    name: __('Classroom activities list'),
    key: 'classroom_activity_ideas',
    icon: 'group',
  },
  HistoricalTimeline: {
    name: __('Timeline of events'),
    key: 'historical_timeline',
    icon: 'recent_outline',
  },
  ReadingList: {
    name: __('Reading list'),
    key: 'reading_list',
    icon: 'library',
  },
  MapOfHistoricalEvents: {
    name: __('Map of historical events'),
    key: 'map_of_historical_events',
    icon: 'map_outline',
  },
  LearningAssessmentPolls: {
    name: __('Assessment polls'),
    key: 'learning_assessment_polls',
    icon: 'poll_outline',
  },
  Rubric: {
    name: __('Rubric'),
    key: 'rubric',
    icon: 'grade_outline',
  },
  ...(isAppUsing('guidedTemplates') && {
    DiscussionBoard: {
      name: __('Discussion board'),
      key: 'discussion_board',
      icon: 'compass',
    },
  }),
  CustomBoard: {
    name: __('Custom board'),
    key: 'custom_board',
    icon: 'sparkles_outline',
  },
}

// Getting the "key" value from magicTemplateEnum and forming a type like "lesson_plan"
export type MagicTemplateKey = {
  [K in keyof typeof magicTemplateEnum]: typeof magicTemplateEnum[K]['key']
}[keyof typeof magicTemplateEnum]

export const useMagicPadletPanelStore = defineStore('magicPadletPanelStore', () => {
  /**
   * Magic Templates Fields
   */
  const refs = reactive({})

  function setRefValue(key: string, value: string | string[]): void {
    if (!(key in refs)) {
      refs[key] = ref(value)
    } else {
      refs[key].value = value
    }
    // Validate form whenever a ref value is updated
    validateForm()
  }

  function getRefValue(key: string): string | string[] {
    return refs[key].value
  }

  function resetRefs(): void {
    for (const key in refs) {
      if (key in refs) {
        delete refs[key]
      }
    }
  }

  const inputConfig = computed(() => {
    return MAGIC_TEMPLATES[magicTemplateSelected.value.key].inputConfig
  })

  /**
   * Validation
   */
  const isFormValid = ref(false)

  function isFieldEmpty(field: string | string[]): boolean {
    if (Array.isArray(field)) {
      return field.length === 0
    } else {
      return field.trim().length === 0
    }
  }

  function validateForm(): void {
    let isValid = true
    for (const config of inputConfig.value) {
      const value = refs[config.refKey].value
      if (config.optional !== true) {
        if (isFieldEmpty(value)) {
          isValid = false
          break
        }
      }
    }
    isFormValid.value = isValid
  }

  /**
   * Magic Template Selector
   */
  const xMagicTemplateSelector = ref(false)
  const magicTemplateSelected = ref<MagicTemplate>(magicTemplateEnum.CustomBoard)
  const magicTemplatesArray = ref<MagicTemplate[]>(Object.values(magicTemplateEnum))

  function toggleMagicTemplateSelector(): void {
    xMagicTemplateSelector.value = !xMagicTemplateSelector.value
  }

  function setMagicTemplateSelected(template: MagicTemplate): void {
    resetRefs()
    magicTemplateSelected.value = template
  }

  /**
   * Magic Template Selector Anchor
   */
  const magicTemplateSelectorAnchor = ref<Partial<PopoverAnchor>>({})
  function updateMagicTemplateSelectorAnchor(anchor: Partial<PopoverAnchor>): void {
    magicTemplateSelectorAnchor.value = anchor
  }

  /**
   * Creation
   */
  const status = ref(MagicPadletPanelStatus.Initial)
  const isCreatingMagicPadlet = computed(() => status.value === MagicPadletPanelStatus.Creating)
  const isAtQuota = computed(() => status.value === MagicPadletPanelStatus.AtQuota)
  const isExample = ref(false)
  const includeImages = ref(true)

  async function generateMagicPadlet({
    currentLibraryId,
    includeImages,
    magicTemplateKey,
  }: {
    currentLibraryId: LibraryId | undefined
    includeImages: boolean
    magicTemplateKey: MagicTemplateKey
  }): Promise<void> {
    try {
      status.value = MagicPadletPanelStatus.Creating

      // For the new magic templates
      // Get form data from refs
      const formData = {}
      for (const key in refs) {
        if (key === 'userPrompt') {
          isExample.value = isExamplePrompt(refs[key].value)
        }
        formData[key] = refs[key].value
      }

      const response: JsonAPIResponse<MagicWallCreationResult> = await PadletApi.Wall.createMagic({
        currentLibraryId,
        isExample: isExample.value,
        includeImages,
        magicTemplateKey,
        formData,
      })
      const statusLink = (response?.data as JsonAPIResource<MagicWallCreationResult>)?.attributes.url

      // Query the status url until it gets us a response, then use the response.
      const { data }: JsonAPIResponse<MagicWallCreationStatusResult> = await poll({
        pollingUrl: statusLink,
        validationCallback: ({ data }) => data?.attributes?.status !== 'in_progress', // If there is no data, this validation will fail too
        options: { intervalSecs: 5, maxAttempts: 60, method: HTTPMethod.get },
      })

      const statusResult = (data as JsonAPIResource<MagicWallCreationStatusResult>)?.attributes

      switch (statusResult?.status) {
        case 'at_quota':
          status.value = MagicPadletPanelStatus.AtQuota
          break
        case 'failed':
          status.value = MagicPadletPanelStatus.Errored
          break
      }

      if (statusResult.status !== 'success') {
        const errorMessage = `Magic wall creation failed${statusResult.status === 'at_quota' ? ' due to quota' : ''}`
        const e = new Error(errorMessage)
        captureException(e)
      }

      // Wall creation was successful
      const logData = {
        currentLibraryId,
        isExample: isExample.value,
        magicTemplateKey,
        ...formData,
      }
      logSuccess(logData)
      // Add a 10ms delay so that the log request is sent before the redirect happens
      setTimeout(() => {
        safeStorage.setItem('createdWithAi', 'true')
        navigateTo(statusResult.wall_url, { target: device.electronApp ? '_blank' : '_self' })
        if (device.electronApp) {
          closeMagicPadletPanel()
        }
      }, 10)
    } catch (e) {
      status.value = MagicPadletPanelStatus.Errored
      if (e instanceof FetchError) {
        captureFetchException(e, { source: 'MagicWallCreation' })
      } else {
        captureException(e)
      }
    }
  }

  function logSuccess(logData: Record<string, any>): void {
    try {
      // Formatting
      for (const key in logData) {
        if (logData[key] === undefined || logData[key] === null || logData[key] === '') {
          delete logData[key]
        }
      }
      if (logData.grades) {
        logData.grades = logData.grades.join(', ')
      }
      if (logData.magicTemplateKey !== 'custom_board') {
        delete logData.role
        delete logData.isExample
      }

      trackEvent('Magic Padlet', 'Created magic padlet', logData.magicTemplateKey, null, logData)
    } catch (e) {
      captureException(e)
    }
  }

  function flipImageToggle(): void {
    includeImages.value = !includeImages.value
  }

  /**
   * Panel Actions
   */
  const xMagicPadletPanel = ref(false)

  function openMagicPadletPanel(): void {
    xMagicPadletPanel.value = true
    status.value = MagicPadletPanelStatus.Initial
    isExample.value = false
  }

  function closeMagicPadletPanel(): void {
    xMagicPadletPanel.value = false
  }

  /**
   * Multiline Input Focus
   */
  const isMultilineInputFocused = ref(false)
  function setIsMultilineInputFocused(value: boolean): void {
    isMultilineInputFocused.value = value
  }
  return {
    /**
     * Magic Templates Fields
     */
    refs,
    setRefValue,
    getRefValue,
    inputConfig,

    /**
     * Validation
     */
    isFormValid,
    validateForm,

    /**
     * Magic Template Selector
     */
    xMagicTemplateSelector,
    magicTemplateSelected,
    magicTemplatesArray,
    magicTemplateSelectorAnchor,
    toggleMagicTemplateSelector,
    updateMagicTemplateSelectorAnchor,
    setMagicTemplateSelected,

    /**
     * Creation
     */
    status,
    isExample,
    includeImages,
    isCreatingMagicPadlet,
    isAtQuota,
    generateMagicPadlet,
    flipImageToggle,

    /**
     * Panel Actions
     */
    xMagicPadletPanel,
    openMagicPadletPanel,
    closeMagicPadletPanel,

    /**
     * Multiline Input Focus
     */
    isMultilineInputFocused,
    setIsMultilineInputFocused,
  }
})

export { MagicPadletPanelStatus, magicTemplateEnum }
